import React from "react";
import { motion } from "framer-motion";
import { Fuel, ShoppingBag, Flame, Truck, BedDouble } from "lucide-react";
import { site } from "@/lib/siteContent";

const iconMap = { Fuel, ShoppingBag, Flame, Truck, BedDouble };

function BusinessCard({ b, i, featured = false }) {
  const Icon = iconMap[b.icon] || Fuel;

  return (
    <motion.article
      data-testid={`business-card-${b.key}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden bg-navy text-white ${
        featured ? "lg:col-span-2 lg:row-span-2 min-h-[520px]" : "min-h-[360px]"
      }`}
    >
      <img
        src={b.image}
        alt={b.title}
        className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy/60 to-navy/10" />

      <div className="relative h-full flex flex-col justify-between p-8 lg:p-10">
        <div className="flex items-start justify-between">
          <div className="w-14 h-14 bg-gold/15 backdrop-blur-md border border-gold/40 flex items-center justify-center">
            <Icon size={22} className="text-gold" strokeWidth={1.5} />
          </div>
          <span className="text-[10px] uppercase tracking-[0.28em] text-white/50 font-bold">
            0{i + 1}
          </span>
        </div>

        <div>
          <h3 className={`font-display font-medium tracking-tight text-white ${featured ? "text-4xl lg:text-5xl" : "text-2xl lg:text-3xl"}`}>
            {b.title}
          </h3>
          <p className={`mt-4 text-white/75 leading-relaxed ${featured ? "text-base lg:text-lg max-w-md" : "text-sm"}`}>
            {b.description}
          </p>

          <div className="mt-6 inline-flex items-center gap-2 text-gold/70 text-[11px] font-bold uppercase tracking-[0.22em]">
            <span className="h-px w-8 bg-gold/50" />
            0{i + 1} / DSS Group
          </div>
        </div>
      </div>

      {/* Hover edge accent */}
      <div className="absolute bottom-0 left-0 h-1 w-0 bg-gold group-hover:w-full transition-all duration-700" />
    </motion.article>
  );
}

export default function Businesses() {
  const items = site.businesses;
  return (
    <section
      id="businesses"
      data-testid="businesses-section"
      className="relative bg-ash py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 lg:mb-20"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold-dark">
                Our Businesses
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy font-medium leading-[1.05] tracking-tight">
              A diversified group<br />
              creating <span className="italic text-gold-dark">enduring value.</span>
            </h2>
          </div>
          <p className="max-w-md text-navy/70 text-base leading-relaxed">
            Five complementary divisions — built on operational excellence, customer obsession and disciplined growth across South Africa.
          </p>
        </motion.div>

        {/* Bento grid: first card is featured */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-4 lg:gap-6">
          <BusinessCard b={items[0]} i={0} featured />
          <BusinessCard b={items[1]} i={1} />
          <BusinessCard b={items[2]} i={2} />
          <BusinessCard b={items[3]} i={3} />
          <BusinessCard b={items[4]} i={4} />
        </div>
      </div>
    </section>
  );
}
